import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, Calendar, Sparkles, MessageSquare, MessageCircle, Trash2, Pencil, Search as SearchIcon, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const statusColor = { hot: "#f97316", warm: "#eab308", cold: "#3b82f6", new: "#71717a" };
const StatusBadge = ({ s }) => (
  <span className="text-xs uppercase tracking-wide px-2.5 py-1 rounded-sm font-semibold" style={{ backgroundColor: `${statusColor[s]}22`, color: statusColor[s] }}>{s}</span>
);

export default function Leads() {
  const { company } = useOutletContext();
  const [leads, setLeads] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const openLead = async (id) => {
    setSelected(id); setDetail(null);
    const res = await api.get(`/leads/${id}`);
    setDetail(res.data);
  };

  const changeStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success("Lead status updated");
    load();
    if (detail) setDetail({ ...detail, lead: { ...detail.lead, status } });
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead and any linked booking? This can't be undone.")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    setSelected(null);
    load();
  };

  const [editLead, setEditLead] = useState(null);
  const openLeadEdit = (l) => setEditLead({
    name: l.name || "", phone: l.phone || "", email: l.email || "",
    address: l.address || "", preferred_date: l.preferred_date || "", job_description: l.job_description || "",
  });
  const setL = (k) => (e) => setEditLead((f) => ({ ...f, [k]: e.target.value }));
  const saveLeadEdit = async () => {
    const res = await api.patch(`/leads/${detail.lead.id}`, {
      name: editLead.name.trim() || null, phone: editLead.phone.trim() || null,
      email: editLead.email.trim() || null, address: editLead.address.trim() || null,
      preferred_date: editLead.preferred_date.trim() || null, job_description: editLead.job_description.trim() || null,
    });
    setDetail((d) => ({ ...d, lead: { ...d.lead, ...res.data } }));
    setEditLead(null);
    toast.success("Lead updated");
    load();
  };

  const generateFollowup = async () => {
    setGenLoading(true);
    try {
      const res = await api.post(`/leads/${selected}/generate-followup`);
      setDetail((d) => ({ ...d, followups: [res.data, ...d.followups] }));
      toast.success("AI follow-up drafted");
    } catch { toast.error("Could not generate follow-up"); }
    finally { setGenLoading(false); }
  };

  const markSent = async (fid) => {
    await api.post("/followups/send", { followup_id: fid });
    setDetail((d) => ({ ...d, followups: d.followups.map((f) => f.id === fid ? { ...f, status: "sent" } : f) }));
  };

  const intlPhone = (p) => { let d = (p || "").replace(/\D/g, ""); if (d.startsWith("0")) d = "44" + d.slice(1); return d; };

  const sendVia = (channel, f) => {
    const lead = detail?.lead || {};
    const msg = f.message || "";
    if (channel === "sms") {
      window.location.href = `sms:${lead.phone}?&body=${encodeURIComponent(msg)}`;
    } else if (channel === "whatsapp") {
      window.open(`https://wa.me/${intlPhone(lead.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
    } else if (channel === "email") {
      const subject = `Following up${lead.job_description ? " on your " + lead.job_description.toLowerCase() : ""}${company?.name ? " — " + company.name : ""}`;
      window.location.href = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(msg)}`;
    }
    markSent(f.id);
    toast.success("Opening your app — follow-up marked as sent");
  };

  if (!leads) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const lq = query.trim().toLowerCase();
  const filtered = !lq ? leads : leads.filter((l) =>
    [l.name, l.phone, l.email, l.trade, l.job_description, l.summary, l.address].filter(Boolean).join(" ").toLowerCase().includes(lq)
  );

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">LEADS</h1>
        <p className="text-muted-foreground mt-1">{leads.length} active — booked leads move to Bookings to keep this tidy</p>
      </div>

      {leads.length > 0 && (
        <div className="relative max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="leads-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, number, email or job…" className="rounded-sm bg-[#0f0f11] border-white/10 pl-9" />
        </div>
      )}

      {leads.length === 0 ? (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-12 text-center text-muted-foreground">
          No leads yet. Generate one from the <a href="/c/demo" className="text-primary underline">live demo</a>.
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#18181b] border border-white/10 rounded-sm p-10 text-center text-muted-foreground">No leads match "{query}".</div>
      ) : (
        <div className="hidden md:block bg-[#18181b] border border-white/10 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-medium">Caller</th>
                <th className="px-5 py-3 font-medium">Trade</th>
                <th className="px-5 py-3 font-medium">Job</th>
                <th className="px-5 py-3 font-medium">Urgency</th>
                <th className="px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} onClick={() => openLead(l.id)} data-testid={`lead-row-${l.id}`} className="border-b border-white/5 last:border-0 hover:bg-secondary/50 cursor-pointer transition-[background-color] duration-150">
                  <td className="px-5 py-4 font-medium">{l.name || "Unknown"}<div className="text-xs text-muted-foreground">{l.phone || "no phone"}</div></td>
                  <td className="px-5 py-4">{l.trade || "—"}</td>
                  <td className="px-5 py-4 max-w-xs truncate text-muted-foreground">{l.job_description || l.summary || "—"}</td>
                  <td className="px-5 py-4 capitalize text-muted-foreground">{(l.urgency || "—").replace("_", " ")}</td>
                  <td className="px-5 py-4"><StatusBadge s={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="md:hidden space-y-3" data-testid="leads-mobile-list">
          {filtered.map((l) => (
            <button key={l.id} onClick={() => openLead(l.id)} data-testid={`lead-card-${l.id}`} className="w-full text-left bg-[#18181b] border border-white/10 rounded-sm p-4 hover:border-primary/40 transition-[border-color,transform] duration-150 active:scale-[0.99]">
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <div className="min-w-0">
                  <p className="font-medium truncate">{l.name || "Unknown"}</p>
                  <p className="text-xs text-muted-foreground">{l.phone || "no phone"}</p>
                </div>
                <StatusBadge s={l.status} />
              </div>
              <p className="text-sm text-muted-foreground truncate">{l.job_description || l.summary || "No job details"}</p>
              <div className="flex items-center gap-2 mt-2.5 text-xs text-muted-foreground">
                <span className="px-2 py-0.5 rounded-sm bg-secondary">{l.trade || "—"}</span>
                {l.urgency && <span className="capitalize">· {(l.urgency || "").replace("_", " ")}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground max-w-3xl max-h-[88vh] overflow-y-auto rounded-sm p-0" data-testid="lead-detail">
          {!detail ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div>
              <div className="flex items-start justify-between p-6 border-b border-white/10">
                <div>
                  <DialogTitle className="font-display text-3xl tracking-tight font-normal">{detail.lead.name || "Unknown caller"}</DialogTitle>
                  <DialogDescription className="sr-only">Lead details, transcript and AI follow-up options</DialogDescription>
                  <div className="flex items-center gap-2 mt-2"><StatusBadge s={detail.lead.status} />
                    <span className="text-sm text-muted-foreground">{detail.lead.trade || "—"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={detail.lead.status} onValueChange={(v) => changeStatus(detail.lead.id, v)}>
                    <SelectTrigger data-testid="status-select" className="w-32 rounded-sm bg-secondary border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                      {["hot", "warm", "cold", "new"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button data-testid="edit-lead-btn" onClick={() => openLeadEdit(detail.lead)} variant="outline" size="icon" className="rounded-sm border-white/10"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid="delete-lead-btn" onClick={() => deleteLead(detail.lead.id)} variant="outline" size="icon" className="rounded-sm border-white/10 text-muted-foreground hover:text-red-400 hover:border-red-400/40"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>

              {editLead ? (
                <div className="p-6 border-b border-white/10 space-y-3" data-testid="lead-edit-form">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Name</Label><Input data-testid="edit-lead-name" value={editLead.name} onChange={setL("name")} className="rounded-sm bg-[#0f0f11] border-white/10" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Phone</Label><Input data-testid="edit-lead-phone" value={editLead.phone} onChange={setL("phone")} className="rounded-sm bg-[#0f0f11] border-white/10" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Email</Label><Input data-testid="edit-lead-email" value={editLead.email} onChange={setL("email")} className="rounded-sm bg-[#0f0f11] border-white/10" /></div>
                    <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Preferred / callback</Label><Input data-testid="edit-lead-preferred" value={editLead.preferred_date} onChange={setL("preferred_date")} className="rounded-sm bg-[#0f0f11] border-white/10" /></div>
                  </div>
                  <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Address</Label><Input data-testid="edit-lead-address" value={editLead.address} onChange={setL("address")} className="rounded-sm bg-[#0f0f11] border-white/10" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job details</Label><Input data-testid="edit-lead-job" value={editLead.job_description} onChange={setL("job_description")} className="rounded-sm bg-[#0f0f11] border-white/10" /></div>
                  <div className="flex gap-2">
                    <Button data-testid="save-lead-btn" onClick={saveLeadEdit} className="rounded-sm bg-primary hover:bg-orange-600 text-white">Save changes</Button>
                    <Button onClick={() => setEditLead(null)} variant="outline" className="rounded-sm border-white/10">Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3 p-6 border-b border-white/10 text-sm">
                    <Info icon={Phone} label="Phone" value={detail.lead.phone} />
                    <Info icon={Mail} label="Email" value={detail.lead.email} />
                    <Info icon={MapPin} label="Address" value={detail.lead.address} />
                    <Info icon={Calendar} label="Preferred" value={detail.lead.preferred_date} />
                    {detail.lead.contact_preference && (
                      <Info icon={detail.lead.contact_preference === "call" ? Phone : MessageSquare} label="Prefers" value={detail.lead.contact_preference === "call" ? "Phone call" : "Text message"} />
                    )}
                  </div>
                  {detail.lead.job_description && (
                    <div className="p-6 border-b border-white/10">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Job Details</p>
                      <p className="text-sm">{detail.lead.job_description}</p>
                    </div>
                  )}
                </>
              )}

              {detail.job && (
                <div className="p-6 border-b border-white/10 bg-green-500/5">
                  <p className="text-xs uppercase tracking-wider text-green-500 mb-1">Booking · {detail.job.status}</p>
                  <p className="text-sm">{detail.job.trade} · {detail.job.slot_start ? new Date(detail.job.slot_start).toLocaleString() : `Requested: ${detail.job.requested_date}`}{detail.job.deposit_status && detail.job.deposit_status !== "none" ? ` · Deposit ${detail.job.deposit_status}` : ""}</p>
                </div>
              )}

              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Follow-ups</p>
                  {company?.is_pro === false ? (
                    <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-sm" data-testid="followup-pro-lock">AI follow-ups · Pro</span>
                  ) : (
                  <Button data-testid="generate-followup-btn" onClick={generateFollowup} disabled={genLoading} size="sm" className="rounded-sm bg-primary hover:bg-orange-600 text-white gap-2">
                    {genLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI Draft
                  </Button>
                  )}
                </div>
                {company?.is_pro === false ? (
                  <p className="text-sm text-muted-foreground">AI follow-up messages are a <span className="text-primary font-medium">Pro</span> feature. Upgrade on the Billing tab to have Ava draft the perfect chase-up for every lead.</p>
                ) : detail.followups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No follow-ups yet. Generate an AI-drafted message, then send it by text, WhatsApp or email.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.followups.map((f) => (
                      <div key={f.id} className="bg-secondary rounded-sm p-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <p className="text-sm flex-1">{f.message}</p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {f.status === "sent" && <span className="text-xs text-green-500 mr-1">Sent ✓</span>}
                          {detail.lead.phone && (
                            <>
                              <Button data-testid={`followup-text-${f.id}`} onClick={() => sendVia("sms", f)} size="sm" variant="outline" className="rounded-sm h-7 border-white/10 gap-1 text-xs px-2"><MessageSquare className="h-3 w-3" /> Text</Button>
                              <Button data-testid={`followup-whatsapp-${f.id}`} onClick={() => sendVia("whatsapp", f)} size="sm" variant="outline" className="rounded-sm h-7 border-white/10 gap-1 text-xs px-2"><MessageCircle className="h-3 w-3" style={{ color: "#25d366" }} /> WhatsApp</Button>
                            </>
                          )}
                          {detail.lead.email && (
                            <Button data-testid={`followup-email-${f.id}`} onClick={() => sendVia("email", f)} size="sm" variant="outline" className="rounded-sm h-7 border-white/10 gap-1 text-xs px-2"><Mail className="h-3 w-3" /> Email</Button>
                          )}
                          {!detail.lead.phone && !detail.lead.email && <span className="text-xs text-yellow-400">No contact details left</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Call Transcript</p>
                <div className="space-y-2">
                  {detail.transcript.map((m, i) => (
                    <div key={i} className={`text-sm px-3 py-2 rounded-sm max-w-[85%] ${m.role === "user" ? "bg-secondary ml-auto" : "bg-[#1c1c1f] border border-white/5"}`}>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground block mb-0.5">{m.role === "user" ? "Caller" : "Ava"}</span>
                      {m.content}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5">
    <Icon className="h-4 w-4 text-primary shrink-0" />
    <div><span className="text-xs text-muted-foreground block">{label}</span><span>{value || "—"}</span></div>
  </div>
);
